<div class="load-details-shell company-detail-shell fade-in" [class.rtl]="languageService.currentLanguage() === 'ar'">
  <a class="back-button" routerLink="/directory">
    <fa-icon [icon]="icons.arrowLeft"></fa-icon>
    Back to directory
  </a>

  @if (entry(); as entry) {
    <section class="page-hero">
      <div class="company-logo hero-company-logo">
        @if (logoUrl(); as logo) {
          <img [src]="logo" [alt]="entry.name || 'Company logo'" (error)="logoFailed = true" />
        } @else {
          <span>{{ logoInitial() }}</span>
        }
      </div>
      <div class="hero-main">
        <span class="eyebrow">Company Profile</span>
        <h1 class="hero-title">
          @if (entry.name) {
            {{ entry.name }}
          } @else if (entry.companyType) {
            {{ companyTypeLabel(entry.companyType) }}
          } @else {
            Logistics Company
          }
        </h1>

        <div class="hero-meta">
          <span class="status-pill">{{ companyTypeLabel(entry.companyType) }}</span>
          @if (entry.companyClass) {
            <span class="status-pill">{{ entry.companyClass }}</span>
          }
          @if (entry.verificationStatus && entry.verificationStatus !== 'UNVERIFIED') {
            <span class="status-pill">
              <fa-icon [icon]="icons.check"></fa-icon>
              {{ entry.verificationStatus }}
            </span>
          }
        </div>
      </div>

      <div class="hero-side">
        <div class="stat-card">
          <fa-icon [icon]="icons.shield"></fa-icon>
          <span class="stat-value">{{ entry.trustScore ?? '--' }}</span>
          <span class="stat-label">Trust score</span>
        </div>
      </div>
    </section>

    <div class="details-grid">
      <div class="panel-column main-column">
        <section class="market-panel">
          <header class="panel-header">
            <div>
              <div class="panel-kicker">Activity</div>
              <h2>Company Activity</h2>
              <p>Load history and current marketplace activity for this company.</p>
            </div>
          </header>

          <div class="metrics-grid">
            <div class="metric-card">
              <span><fa-icon [icon]="icons.truck"></fa-icon> Total loads</span>
              <strong>{{ entry.totalLoadsPosted }}</strong>
            </div>
            <div class="metric-card">
              <span><fa-icon [icon]="icons.check"></fa-icon> Completed</span>
              <strong>{{ entry.completedLoads }}</strong>
              <small>{{ entry.cancelledLoads }} cancelled</small>
            </div>
            <div class="metric-card">
              <span><fa-icon [icon]="icons.chart"></fa-icon> Completion rate</span>
              <strong [style.color]="completionRateColor(entry.completionRate)">
                {{ formatPercent(entry.completionRate) }}
              </strong>
              <div class="completion-bar">
                <div class="completion-bar-fill"
                  [style.width.%]="entry.completionRate"
                  [style.background]="completionRateColor(entry.completionRate)"></div>
              </div>
            </div>
            <div class="metric-card">
              <span><fa-icon [icon]="icons.clock"></fa-icon> Active loads</span>
              <strong>{{ entry.activeLoadsCount }}</strong>
              @if (entry.lastActiveAt) {
                <small>Last active {{ formatDate(entry.lastActiveAt) }}</small>
              }
            </div>
          </div>
        </section>

        <section class="market-panel">
          <header class="panel-header">
            <div>
              <div class="panel-kicker">Services</div>
              <h2>Service Specializations</h2>
              <p>Capabilities and categories associated with this company.</p>
            </div>
          </header>

          @if ((entry.specializations || []).length > 0) {
            <div class="chip-list">
              @for (spec of (entry.specializations || []); track spec) {
                <span class="info-chip">
                  <fa-icon [icon]="icons.briefcase"></fa-icon>
                  {{ spec }}
                </span>
              }
            </div>
          } @else {
            <div class="empty-copy">No service specializations recorded.</div>
          }
        </section>

        <section class="market-panel">
          <header class="panel-header">
            <div>
              <div class="panel-kicker">Coverage</div>
              <h2>Operating Regions</h2>
              <p>Regions and lanes where this company is active.</p>
            </div>
          </header>

          @if ((entry.operatingRegions || []).length > 0) {
            <div class="chip-list">
              @for (region of (entry.operatingRegions || []); track region) {
                <span class="info-chip">
                  <fa-icon [icon]="icons.location"></fa-icon>
                  {{ region }}
                </span>
              }
            </div>
          } @else {
            <div class="empty-copy">No operating regions recorded.</div>
          }
        </section>
      </div>

      <aside class="panel-column side-column">
        <section class="action-card trust-panel">
          <div class="panel-kicker">
            <fa-icon [icon]="icons.shield"></fa-icon>
            Trust
          </div>
          <h2>Trust Profile</h2>

          <div class="trust-score-big">
            <div class="trust-score-circle {{ trustScoreClass(entry.trustScore) }}">
              {{ entry.trustScore ?? '--' }}
            </div>
            <div>
              <strong>{{ entry.trustBadge || 'Trust profile not available' }}</strong>
              <span>Trust score out of 100</span>
            </div>
          </div>

          <div class="details-list">
            <div class="detail-row">
              <span>Company class</span>
              <strong>{{ entry.companyClass || 'N/A' }}</strong>
            </div>
            <div class="detail-row">
              <span>Verification</span>
              <strong>{{ entry.verificationStatus || 'N/A' }}</strong>
            </div>
            <div class="detail-row">
              <span>Type</span>
              <strong>{{ entry.companyType || 'N/A' }}</strong>
            </div>
            <div class="detail-row">
              <span>Completion</span>
              <strong>{{ formatPercent(entry.completionRate) }}</strong>
            </div>
          </div>
        </section>

        @if (entry.primaryContact && (entry.primaryContact.email || entry.primaryContact.phone)) {
          <section class="action-card contact-panel">
            <div class="panel-kicker">
              <fa-icon [icon]="icons.phone"></fa-icon>
              Contact
            </div>
            <h2>Contact Company</h2>
            <p class="contact-name">{{ contactName(entry) }}</p>

            <div class="contact-actions">
              @if (emailHref(entry); as href) {
                <a class="secondary-action contact-action" [href]="href">
                  <fa-icon [icon]="icons.email"></fa-icon>
                  Email
                </a>
              }
              @if (phoneHref(entry); as href) {
                <a class="secondary-action contact-action" [href]="href">
                  <fa-icon [icon]="icons.phone"></fa-icon>
                  Call
                </a>
              }
              @if (whatsappHref(entry); as href) {
                <a class="secondary-action contact-action whatsapp-action" [href]="href" target="_blank" rel="noopener">
                  <fa-icon [icon]="icons.whatsapp"></fa-icon>
                  WhatsApp
                </a>
              }
            </div>
          </section>
        }

        <section class="action-card">
          <div class="panel-kicker">
            <fa-icon [icon]="icons.building"></fa-icon>
            Record
          </div>
          <h2>Directory Summary</h2>
          <div class="details-list">
            <div class="detail-row">
              <span>Active loads</span>
              <strong>{{ entry.activeLoadsCount }}</strong>
            </div>
            <div class="detail-row">
              <span>Total loads</span>
              <strong>{{ entry.totalLoadsPosted }}</strong>
            </div>
            <div class="detail-row">
              <span>Last active</span>
              <strong>{{ entry.lastActiveAt ? formatDate(entry.lastActiveAt) : 'N/A' }}</strong>
            </div>
          </div>
        </section>
      </aside>
    </div>
  } @else if (loading()) {
    <div class="state-panel">
      <div class="spinner"></div>
      <p>Loading company details...</p>
    </div>
  } @else if (error) {
    <div class="state-panel error">{{ error }}</div>
  }
</div>
